import React, { useCallback, useEffect } from 'react';
import { ApolloError } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Checkbox, Icon, Typography } from 'glints-aries/es/@next';
import { borderRadius4 } from 'glints-aries/es/@next/utilities/borderRadius';
import { Blue, Neutral } from 'glints-aries/es/@next/utilities/colors';
import { space12, space16 } from 'glints-aries/es/@next/utilities/spacing';
import {
  Control,
  Controller,
  useForm,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { WhatsAppGreen } from '../../../../../common/color';
import {
  COUNTRY_CALLING_CODES,
  NotificationCategory,
  NotificationChannel,
} from '../../../../../common/constants';
import {
  preventNonNumericInputBeforeInput,
  preventNonNumericInputKeyDown,
} from '../../../../../common/helpers/input';
import { PhoneUtils } from '../../../../../common/helpers/phone';
import { useSendWhatsappVerificationToken } from '../../../../../common/hooks/requests/useSendWhatsappVerificationToken';
import { useGetUserNotificationPreferences } from '../../../../../common/hooks/useGetUserNotificationPreferences';
import { useSaveUserNotificationPreferences } from '../../../../../common/hooks/useSaveUserNotificationPreferences';
import { User } from '../../../../../models/User';
import { signUpPageMessages } from '../../../../../modules/CompanyOnboarding/SignUpPage/signUpPageMessages';
import useUpdatePhone from '../../../../../modules/User/hooks/useUpdatePhone';
import { getUser } from '../../../../../selectors/user';
import InputField from '../../../../Fields/InputField';
import { WhatsappVerificationStep } from '../../../../WhatsappVerification/types';
import { useWhatsappVerificationContext } from '../../../../WhatsappVerification/WhatsappVerificationContext/WhatsappVerificationContext';
import {
  FORM_CANCEL_VALUE,
  FORM_ID,
  FORM_SUBMIT_VALUE,
  GraphQLErrorCodes,
} from '../../consts';
import {
  defaultInputNumberFormState,
  errorMessages,
  inputNumberValidationSchema,
} from './constants';
import { CountryCallingCodeDropdown } from './CountryCallingCodeDropdown';
import * as S from './styles.sc';
import { InputNumberFormState } from './types';

interface InputNumberStepProps {
  initialPhoneNumber?: string;
  initialCallingCode?: string;
}

export const InputNumberStep = (props: InputNumberStepProps) => {
  const user: User = useSelector(getUser);
  const contactNumber = user.whatsappNumber || user.phone;
  const {
    data: userNotificationPreferencesData,
    loading: userNotificationPreferencesLoading,
  } = useGetUserNotificationPreferences({
    fetchPolicy: 'network-only',
  });

  const [saveUserNotificationPreferences] = useSaveUserNotificationPreferences({
    refetchQueries: ['getUserNotificationPreferenceCategories'],
  });

  const [sendWhatsappVerificationToken] = useSendWhatsappVerificationToken();

  const applicationNotificationPreferences =
    userNotificationPreferencesData?.getUserNotificationPreferenceCategories.find(
      category => category.name === NotificationCategory.APPLICATION
    );
  const jobNotificationPreferences =
    userNotificationPreferencesData?.getUserNotificationPreferenceCategories.find(
      category => category.name === NotificationCategory.JOB
    );

  const whatsappNotificationPreferenceIdForApplication =
    applicationNotificationPreferences?.userNotificationPreferences.find(
      preference => preference.channel === NotificationChannel.WHATSAPP
    );
  const whatsappNotificationPreferenceIdForJob =
    jobNotificationPreferences?.userNotificationPreferences.find(
      preference => preference.channel === NotificationChannel.WHATSAPP
    );

  const { formatMessage } = useIntl();
  const { nextStep, setLoading, currentStep } =
    useWhatsappVerificationContext();

  const { control, handleSubmit, setValue, setError, clearErrors } =
    useForm<InputNumberFormState>({
      defaultValues: {
        ...defaultInputNumberFormState,
        phone:
          props.initialPhoneNumber ??
          PhoneUtils.phoneMetadata(contactNumber).phone,
        callingCode:
          props.initialCallingCode ??
          PhoneUtils.phoneMetadata(contactNumber).callingCode,
        isEditable:
          currentStep !== WhatsappVerificationStep.ConfirmWhatsappNumber,
      },
      resolver: yupResolver(inputNumberValidationSchema(formatMessage)),
      mode: 'onBlur',
    });

  const handleUpdatePhoneNumber = () => {
    setValue('isEditable', true);
  };
  const handleCancelUpdatePhoneNumber = () => {
    setValue('isEditable', false);
  };

  const { updatePhone } = useUpdatePhone();

  const onSubmit = async (
    data: InputNumberFormState,
    event?: React.BaseSyntheticEvent
  ) => {
    const submitter = (
      event as React.BaseSyntheticEvent<SubmitEvent> | undefined
    )?.nativeEvent?.submitter as HTMLButtonElement | undefined;
    if (
      submitter?.name ===
      FORM_SUBMIT_VALUE[WhatsappVerificationStep.ConfirmWhatsappNumber]
    ) {
      handleUpdatePhoneNumber();
      return;
    }
    if (
      submitter?.name ===
      FORM_CANCEL_VALUE[WhatsappVerificationStep.ConfirmWhatsappNumber]
    ) {
      handleCancelUpdatePhoneNumber();
      return;
    }

    try {
      setLoading(true);
      await updatePhone({
        phone: `${data.callingCode} ${data.phone.replace(/\s/g, '')}`,
      });

      await saveUserNotificationPreferences({
        variables: {
          data: {
            preferences: [
              {
                id: whatsappNotificationPreferenceIdForApplication?.id,
                enabled: data.optIn,
                channel: NotificationChannel.WHATSAPP,
                CategoryId: applicationNotificationPreferences?.id ?? '',
              },
              {
                id: whatsappNotificationPreferenceIdForJob?.id,
                enabled: data.optIn,
                channel: NotificationChannel.WHATSAPP,
                CategoryId: jobNotificationPreferences?.id ?? '',
              },
            ],
          },
        },
      });

      await sendWhatsappVerificationToken();

      nextStep();
    } catch (e) {
      if (e instanceof ApolloError) {
        const errorCode = e.graphQLErrors?.[0]?.extensions?.code;
        if (errorCode === GraphQLErrorCodes.WHATSAPP_HAS_BEEN_VERIFIED) {
          setError('phone', {
            message: formatMessage(errorMessages.hasBeenVerified),
          });
          return;
        }
      }

      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const initializePhoneNumber = useCallback(() => {
    const initialCallingCode =
      props.initialCallingCode ??
      PhoneUtils.phoneMetadata(contactNumber).callingCode ??
      COUNTRY_CALLING_CODES[user.CountryCode.toLowerCase()];
    let initialPhoneNumber = PhoneUtils.formatPhoneNumber(
      props.initialPhoneNumber ?? PhoneUtils.phoneMetadata(contactNumber).phone,
      initialCallingCode
    );

    if (initialPhoneNumber.startsWith('0')) {
      // remove leading 0, ex: 0812345678 -> 812345678
      initialPhoneNumber = initialPhoneNumber.slice(1);
    }

    setValue('phone', initialPhoneNumber);
    setValue('callingCode', initialCallingCode);
    clearErrors('phone');
  }, [
    clearErrors,
    contactNumber,
    props.initialCallingCode,
    props.initialPhoneNumber,
    setValue,
    user.CountryCode,
  ]);

  useEffect(() => {
    setLoading(userNotificationPreferencesLoading);
  }, [userNotificationPreferencesLoading, setLoading]);

  useEffect(() => {
    initializePhoneNumber();
  }, [initializePhoneNumber]);

  return (
    <S.StyledInputNumberStep>
      <Icon
        name="ri-whatsapp-fill"
        fill={WhatsAppGreen}
        width={72}
        height={72}
      />
      <div>
        <Typography variant="subtitle1">
          <Choose>
            <When
              condition={
                currentStep === WhatsappVerificationStep.ConfirmWhatsappNumber
              }
            >
              <FormattedMessage
                id="text-confirm-wa"
                defaultMessage="Confirm your WhatsApp number"
              />
            </When>
            <Otherwise>
              <FormattedMessage
                id="text-add-wa"
                defaultMessage="Add your WhatsApp number"
              />
            </Otherwise>
          </Choose>
        </Typography>
        <Typography variant="body1" color={Neutral.B40}>
          <Choose>
            <When
              condition={
                currentStep === WhatsappVerificationStep.ConfirmWhatsappNumber
              }
            >
              <FormattedMessage
                id="text-confirm-wa-desc"
                defaultMessage="It’s been a while since you last checked this number. We use it for job updates and important messages."
              />
            </When>
            <Otherwise>
              <FormattedMessage
                id="text-add-wa-desc"
                defaultMessage="We’ll use this number to contact you about jobs and important updates."
              />
            </Otherwise>
          </Choose>
        </Typography>
      </div>
      <form
        id={FORM_ID[WhatsappVerificationStep.InputNumber]}
        onSubmit={handleSubmit(onSubmit)}
      >
        <PhoneNumberInput control={control} setValue={setValue} />
        <ConsentCheckBox control={control} />
        <div
          id="reset-phone-number-button"
          onClick={() => {
            initializePhoneNumber();
          }}
        />
      </form>
      <ConfirmWhatsappNumberAdditionalInfo control={control} />
    </S.StyledInputNumberStep>
  );
};

function PhoneNumberInput({
  control,
  setValue,
}: {
  control: Control<InputNumberFormState>;
  setValue: UseFormSetValue<InputNumberFormState>;
}) {
  const { formatMessage } = useIntl();
  const [callingCode = '', isEditable = false] = useWatch({
    control,
    name: ['callingCode', 'isEditable'],
  });

  const handleEditButtonClick = () => {
    // we need to click the update button manually as the button it self has some internal
    // start which needs to triggered
    const updateButton = document.querySelector(
      '#update-phone-number-button'
    ) as HTMLButtonElement | null;
    if (updateButton) {
      updateButton.click();
    }
  };

  return (
    <Controller
      control={control}
      name="phone"
      render={({
        field: { value, onChange, onBlur },
        fieldState: { error },
      }) => (
        <Choose>
          <When condition={isEditable}>
            <S.PhoneInputRow>
              <CountryCallingCodeDropdown
                isWhatsAppInput={true}
                callingCode={callingCode}
                onChange={option => {
                  setValue('callingCode', option.callingCode, {
                    shouldValidate: true,
                  });
                }}
              />
              <div>
                <InputField
                  autoFocus={true}
                  onChange={e => {
                    onChange(
                      PhoneUtils.formatPhoneNumber(e.target.value, callingCode)
                    );
                  }}
                  value={value}
                  onBlur={onBlur}
                  errorText={error?.message}
                  className="phone-number-input"
                  placeholder={formatMessage(
                    signUpPageMessages.enterYourMobileNumber
                  )}
                  fullWidth={true}
                  type="tel"
                  onKeyDown={preventNonNumericInputKeyDown}
                  onBeforeInput={preventNonNumericInputBeforeInput}
                />
                <S.ExamplePhoneNumber>
                  <FormattedMessage
                    id="modules.Settings.Components.UpdatePhone.example.number"
                    defaultMessage="Example: {exampleNumber}"
                    values={{
                      exampleNumber: PhoneUtils.phoneExample(callingCode),
                    }}
                  />
                </S.ExamplePhoneNumber>
              </div>
            </S.PhoneInputRow>
          </When>
          <Otherwise>
            <InputField
              disabled={!isEditable}
              value={value}
              errorText={error?.message}
              className="phone-number-input"
              placeholder={formatMessage(
                signUpPageMessages.enterYourMobileNumber
              )}
              fullWidth={true}
              type="tel"
              suffix={
                <Icon
                  name="ri-pencil-line"
                  fill={Blue.S99}
                  onClick={handleEditButtonClick}
                />
              }
              prefix={`${callingCode} |`}
            />
          </Otherwise>
        </Choose>
      )}
    />
  );
}

function ConsentCheckBox({
  control,
}: {
  control: Control<InputNumberFormState>;
}) {
  const isEditable = useWatch({ control, name: 'isEditable' });
  if (!isEditable) {
    return null;
  }
  return (
    <Controller
      control={control}
      name="optIn"
      render={({ field: { value, onChange } }) => (
        <Checkbox
          checked={value}
          onChange={onChange}
          label={
            <FormattedMessage
              id="text-consent-notification-whatsapp"
              defaultMessage="I consent to receive important job updates on my Whatsapp number"
            />
          }
        />
      )}
    />
  );
}

function ConfirmWhatsappNumberAdditionalInfo({
  control,
}: {
  control: Control<InputNumberFormState>;
}) {
  const { currentStep } = useWhatsappVerificationContext();
  const isEditable = useWatch({ control, name: 'isEditable' });
  if (
    currentStep !== WhatsappVerificationStep.ConfirmWhatsappNumber ||
    isEditable
  ) {
    return null;
  }
  return (
    <StyledBox>
      <Icon name="ri-question-line" width={20} height={20} fill={Blue.S99} />
      <Typography variant="subtitle2">
        <FormattedMessage
          id="text-confirm-wa-footer"
          defaultMessage="Are you still able to receive WhatsApp messages on this number?"
        />
      </Typography>
    </StyledBox>
  );
}

const StyledBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${space12} ${space16};
  border-radius: ${borderRadius4};
  background-color: ${Blue.S08};
  color: ${Neutral.B40};
`;
